// src/pages/EditListingPage.tsx
import { useState, useEffect } from 'react';
import { ArrowLeft, X, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SimpleImageUpload } from '../components/SimpleImageUpload';
import { useRouter } from '../components/Router';

interface Listing {
  id: string;
  brand: string;
  model: string;
  year: number;
  price: number;
  mileage: number;
  fuel_type: string;
  transmission: string;
  description: string;
  contact_phone: string;
  contact_email: string;
  images: string[];
  status: string;
  user_id: string;
}

export function EditListingPage() {
  const { currentPath, navigate } = useRouter();
  const id = currentPath.split('/').pop();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [listing, setListing] = useState<Listing | null>(null);
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    fuel_type: 'Essence',
    transmission: 'Manuelle',
    description: '',
    contact_phone: '',
    contact_email: '',
  });
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    // Wait for auth to complete
    if (authLoading) return;

    if (!user) {
      navigate('/connecter');
      return;
    }
    if (id) {
      fetchListing();
    }
  }, [user, id, authLoading]);

  const fetchListing = async () => {
    try {
      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('id', id)
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setError('Annonce introuvable ou vous n\'avez pas l\'autorisation de la modifier');
        return;
      }

      setListing(data);
      setFormData({
        brand: data.brand,
        model: data.model,
        year: data.year,
        price: data.price.toString(),
        mileage: data.mileage.toString(),
        fuel_type: data.fuel_type,
        transmission: data.transmission,
        description: data.description,
        contact_phone: data.contact_phone,
        contact_email: data.contact_email,
      });
      setImages(data.images || []);
    } catch (error: any) {
      console.error('Error fetching listing:', error);
      setError('Erreur lors du chargement de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (images.length === 0) {
      setError('Veuillez ajouter au moins une image');
      setSubmitting(false);
      return;
    }

    try {
      const { error: updateError } = await supabase
        .from('listings')
        .update({
          brand: formData.brand,
          model: formData.model,
          year: parseInt(formData.year.toString()),
          price: parseFloat(formData.price),
          mileage: parseInt(formData.mileage),
          fuel_type: formData.fuel_type,
          transmission: formData.transmission,
          description: formData.description,
          contact_phone: formData.contact_phone,
          contact_email: formData.contact_email,
          images,
          status: 'pending', // Re-trigger moderation on edit
        })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (updateError) throw updateError;

      alert('Annonce mise à jour avec succès! Elle sera réexaminée par un administrateur.');
      navigate('/mes-annonces');
    } catch (error: any) {
      console.error('Error updating listing:', error);
      setError('Erreur lors de la mise à jour de l\'annonce');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = (imageUrl: string) => {
    setImages([...images, imageUrl]);
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4452C] mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error && !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md w-full text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <a href="/mes-annonces" className="inline-block bg-[#D4452C] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B8381E] transition-colors">Retour à mes annonces</a>
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4452C] focus:border-transparent outline-none transition-all";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-2";

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <a href="/mes-annonces" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
          <ArrowLeft className="h-5 w-5" /> Retour à mes annonces
        </a>
        <div className="bg-white rounded-2xl shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Modifier l'annonce</h1>
          <p className="text-gray-600 mb-8">Modifiez les informations de votre annonce</p>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* ... Form fields remain the same as your input, structure preserved ... */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className={labelClass}>Marque *</label><input type="text" required value={formData.brand} onChange={(e) => setFormData({ ...formData, brand: e.target.value })} className={inputClass} placeholder="Ex: Peugeot" /></div>
              <div><label className={labelClass}>Modèle *</label><input type="text" required value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} className={inputClass} placeholder="Ex: 208" /></div>
              <div><label className={labelClass}>Année *</label><input type="number" required min="1900" max={new Date().getFullYear() + 1} value={formData.year} onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })} className={inputClass} /></div>
              <div><label className={labelClass}>Prix (DT) *</label><input type="number" required min="0" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className={inputClass} placeholder="Ex: 45000" /></div>
              <div><label className={labelClass}>Kilométrage *</label><input type="number" required min="0" value={formData.mileage} onChange={(e) => setFormData({ ...formData, mileage: e.target.value })} className={inputClass} placeholder="Ex: 50000" /></div>
              
              <div>
                <label className={labelClass}>Carburant *</label>
                <select required value={formData.fuel_type} onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })} className={inputClass}>
                  <option value="Essence">Essence</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Hybride">Hybride</option>
                  <option value="Électrique">Électrique</option>
                  <option value="GPL">GPL</option>
                </select>
              </div>
              
              <div>
                <label className={labelClass}>Transmission *</label>
                <select required value={formData.transmission} onChange={(e) => setFormData({ ...formData, transmission: e.target.value })} className={inputClass}>
                  <option value="Manuelle">Manuelle</option>
                  <option value="Automatique">Automatique</option>
                  <option value="Semi-automatique">Semi-automatique</option>
                </select>
              </div>
              
              <div><label className={labelClass}>Téléphone *</label><input type="tel" required value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className={inputClass} placeholder="Ex: +216 20 123 456" /></div>
            </div>
            
            <div><label className={labelClass}>Email *</label><input type="email" required value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className={inputClass} placeholder="Ex: contact@email.com" /></div>
            
            <div><label className={labelClass}>Description *</label><textarea required rows={5} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className={inputClass} placeholder="Décrivez votre véhicule..." /></div>
            
            <div>
              <label className={labelClass}>Images *</label>
              {/* This is the uploader that triggers handleImageUpload */}
              <SimpleImageUpload onImageUploaded={handleImageUpload} />
              
              {/* This displays the list of uploaded images */}
              {images.length > 0 && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img src={image} alt={`Upload ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                      <button type="button" onClick={() => removeImage(index)} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex gap-4 pt-4">
              <a href="/mes-annonces" className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors text-center">Annuler</a>
              <button type="submit" disabled={submitting} className="flex-1 bg-[#D4452C] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#B8381E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {submitting ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}